import {
  SUMMARY_METRIC_COST_PER_HA,
  SUMMARY_TOTAL_COLUMN_PREFIX,
  SUMMARY_TOTAL_COLUMN_UNIT_SUFFIXES,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput, IUnit } from 'interfaces/IInput';
import { InputTaskCategory, type IInputTask } from 'interfaces/IInputTask';
import {
  InputStepType,
  SeedDressRateMode,
  type IInputStep,
  type IInputStepRateCost,
  type IInputStepSeedDress,
} from 'interfaces/IInputStep';
import type { SummaryDataMetric } from '../../../../redux/slices/InsightsSlice';
import {
  convertToHectares,
  convertToTonnes,
} from 'utils/unitConversion';

/**
 * Canonical task-category sets for the Live Panel's Plan/Actual columns —
 * shared across all four panels' compute calls so "Plan only", "Actual
 * only", and "combined" (used for each panel's chart, which shows one
 * overall distribution rather than a Plan/Actual split) always mean the
 * same thing.
 */
export const PLAN_ONLY_CATEGORIES = new Set([InputTaskCategory.Plan]);
export const ACTUAL_ONLY_CATEGORIES = new Set([InputTaskCategory.Actual]);
export const PLAN_AND_ACTUAL_CATEGORIES = new Set([
  InputTaskCategory.Plan,
  InputTaskCategory.Actual,
]);

/**
 * Which farm/paddock tree level the current Insights scope resolves to —
 * mirrors OrganisationWorkspace.tsx's activeTreeNodeKey prefix parsing
 * ('org-' | 'farm-' | 'paddock-' | 'input-'). Threaded down through
 * InputInsights so lenses can gate features that only make sense at a
 * narrow-enough scope (e.g. Summary 2's per-crop spend timeline, which
 * needs Paddock level or lower to reliably resolve to one crop).
 */
export type InsightsScopeLevel =
  | 'org'
  | 'farm'
  | 'crop'
  | 'paddock'
  | 'input';

/**
 * Derives InsightsScopeLevel from a tree node key. Single source of truth for
 * the 'org-'/'farm-'/'crop-'/'paddock-'/'input-' prefix parsing — used by both
 * OrganisationWorkspace.tsx (to gate lens features) and RightPanelShell.tsx
 * (to gate Live Panel visibility, OP-2333 — the panel only makes sense once
 * a specific paddock or plan is in scope).
 */
export const deriveScopeLevel = (
  activeTreeNodeKey: string | null | undefined,
  prefixes: {
    input: string;
    paddock: string;
    farm: string;
    crop: string;
    org: string;
  }
): InsightsScopeLevel | undefined => {
  if (!activeTreeNodeKey) return undefined;
  if (activeTreeNodeKey.startsWith(prefixes.input)) return 'input';
  if (activeTreeNodeKey.startsWith(prefixes.paddock)) return 'paddock';
  if (activeTreeNodeKey.startsWith(prefixes.farm)) return 'farm';
  if (activeTreeNodeKey.startsWith(prefixes.crop)) return 'crop';
  if (activeTreeNodeKey.startsWith(prefixes.org)) return 'org';
  return undefined;
};

/* ═══════════════════════════════════════════════════════════════
   Quantity display scaling — shared between SummaryGrid and
   SummaryView's copy-to-clipboard so both present the same numbers.
   ═══════════════════════════════════════════════════════════════ */

/** Mass values of 1000kg or more read better as tonnes; liquid never auto-scales. */
export const MASS_TONNE_THRESHOLD_KG = 1000;

/**
 * Given a value already expressed in a row/footer's base unit (kg or L,
 * per `computeSummary`'s per-kind normalisation), pick the nicer display
 * unit and the value converted into it. Only mass auto-switches to tonnes
 * above the threshold; liquid always stays in litres; any other/undefined
 * unit passes through unchanged.
 */
export const scaleForDisplay = (
  value: number,
  baseUnit: string | undefined
): { value: number; unit: string | undefined } =>
  baseUnit === 'kg' && Math.abs(value) >= MASS_TONNE_THRESHOLD_KG
    ? { value: value / 1000, unit: 't' }
    : { value, unit: baseUnit };

/**
 * Label for the grid's "Total" column, made specific to the selected
 * metric (e.g. "Total cost ($)") rather than a bare, vague "Total".
 * $/ha is a rate rather than a summed value, so it's shown without the
 * "Total" prefix. A label that already starts with "Total" (e.g. "Total
 * Area") isn't prefixed twice, and gets its unit when it has none.
 */
export const resolveTotalColumnLabel = (
  dataMetric: SummaryDataMetric,
  metricLabel: string
): string => {
  if (dataMetric === SUMMARY_METRIC_COST_PER_HA) return metricLabel;
  const name = metricLabel.replace(/^total\s+/i, '');
  const unitSuffix = SUMMARY_TOTAL_COLUMN_UNIT_SUFFIXES[dataMetric];
  const label = `${SUMMARY_TOTAL_COLUMN_PREFIX} ${name.charAt(0).toLowerCase()}${name.slice(1)}`;
  return unitSuffix && !name.includes('(') ? `${label} ${unitSuffix}` : label;
};

/* ═══════════════════════════════════════════════════════════════
   Area resolution — fallback chain then convert to hectares
   ═══════════════════════════════════════════════════════════════ */

/**
 * Resolve raw area value and its corresponding unit from the fallback
 * chain: step.area → task.area → input.zone.zoneArea.
 */
export const resolveAreaWithUnit = (
  step: IInputStep,
  task: IInputTask,
  input: IInput
): { value: number; unit: IUnit | null } => {
  if (step.area != null) {
    return { value: step.area, unit: step.areaUnit };
  }
  if (task.area != null) {
    return { value: task.area, unit: task.areaUnit };
  }
  return {
    value: input.zone?.zoneArea ?? 0,
    unit: input.zone?.areaUnit ?? null,
  };
};

/**
 * Resolve area and convert to hectares in one step.
 * Returns 0 when the raw value is ≤ 0 or conversion fails.
 */
export const resolveAreaHa = (
  step: IInputStep,
  task: IInputTask,
  input: IInput,
  gUnits: GUnit[]
): number => {
  const { value, unit } = resolveAreaWithUnit(step, task, input);
  if (value <= 0) return 0;
  return convertToHectares(value, unit, gUnits) ?? value;
};

/* ═══════════════════════════════════════════════════════════════
   Seed Dress helper — resolve linked Seed step rate in kg/ha
   ═══════════════════════════════════════════════════════════════ */

/**
 * For SeedDress rateMode=Per100Kg, find the linked Seed step in the
 * same task and return its rate converted to kg/ha.
 *
 * Mobile app Seed.rate units: kg or t.
 * If the unit resolves to tonnes, multiply by 1000 to get kg.
 * Otherwise assume kg (most common case).
 */
export const findLinkedSeedRateKg = (
  seedDress: IInputStepSeedDress,
  task: { steps: IInputStep[] },
  gUnits: GUnit[]
): number => {
  if (!seedDress.linkedStepId) return 0;
  const seedStep = task.steps.find(
    (s) =>
      s.stepId === seedDress.linkedStepId &&
      s.stepType === InputStepType.Seed
  );
  if (!seedStep) return 0;
  const seed = seedStep as IInputStepRateCost;
  const seedRate = seed.rate ?? 0;
  if (seedRate <= 0) return 0;

  // Try to convert to tonnes; if it succeeds the unit is a weight unit
  const asTonnes = convertToTonnes(seedRate, seed.appUnit, gUnits);
  if (asTonnes != null) {
    // Convert tonnes → kg
    return asTonnes * 1000;
  }
  // Fallback: assume already in kg/ha
  return seedRate;
};

/* ═══════════════════════════════════════════════════════════════
   Effective rate — SeedDress Per100Kg substitution, shared across
   Gross Margin, Summary, the Preview page, and the Tasks grid so
   the rateMode branch is defined in exactly one place.
   ═══════════════════════════════════════════════════════════════ */

/**
 * Rate to use in cost/quantity calculations for a rate-cost step.
 * For most step types this is just the step's own rate. For SeedDress
 * steps in Per100Kg mode, the rate is expressed per 100kg of the linked
 * Seed step's rate, so this substitutes (rate / 100) * seedRateKg.
 *
 * Returns `null` only when a Per100Kg SeedDress step's linked Seed step
 * can't be resolved — callers that want to treat a merely-zero rate the
 * same way should also check `<= 0` themselves, since a resolvable
 * SeedDress step with rate 0 legitimately returns 0, not null.
 */
export const resolveEffectiveRate = (
  step: IInputStep,
  task: { steps: IInputStep[] },
  gUnits: GUnit[]
): number | null => {
  const rate = 'rate' in step ? (step.rate ?? 0) : 0;

  if (
    step.stepType === InputStepType.SeedDress &&
    step.rateMode === SeedDressRateMode.Per100Kg
  ) {
    const seedRateKg = findLinkedSeedRateKg(step, task, gUnits);
    if (seedRateKg <= 0) return null;
    return (rate / 100) * seedRateKg;
  }

  return rate;
};
